import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NewcastleCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Newcastle Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="Newcastle City Council earns a RED best-practice scorecard and reports 0% preventative highway spend for five years running, while U-roads in RED condition doubled to 28.9%. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/newcastle"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Newcastle', url: '/councils/newcastle' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Newcastle has a GREEN spend rating — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and whether the council had reasonable knowledge of it under Section 58. Newcastle is AMBER overall for condition, RED for best practice, and its own report records 0% preventative spend and 100% reactive spend across every year published — meaning repairs happen after damage, not before.',
          },
          {
            question: 'What does Newcastle\'s RED best-practice rating mean for my claim?',
            answer: 'The DfT Best Practice scorecard is RED — one of the weakest ratings a council can hold. Newcastle\'s transparency report shows 0% of highway spend classified as preventative in every year from 2020/21 to 2025/26, with 100% reactive. The council itself states works are "currently reactive treatments" and that implementing preventative programmes is only an "aspiration". That undercuts any Section 58 argument that the council proactively managed the road before your incident.',
          },
          {
            question: 'My pothole was on a residential street — does the U-road data help?',
            answer: 'Unclassified roads make up 783.4km — 74.5% of Newcastle\'s 1,051.8km carriageway network. On comparable pre-2024 survey data, U-roads in RED condition rose from 14.17% in 2020/21 to 28.9% in 2023/24 — more than doubling in four years. The network is surveyed only once every two years, so a defect can form and worsen in the gap between surveys.',
          },
          {
            question: 'The 2024 condition figures look worse — are they reliable?',
            answer: 'Treat 2024 separately. Newcastle switched survey provider from SCANNER/CVI/DVI to Gaist PAS 2161:2024 surveys in 2024, and the council states this data "cannot be directly compared with historic data due to the change in the classification tier system." The 2024 A-road RED figure of 5.96% and B/C RED of 9.51% use a different ruler — but the council\'s own admission that historic and current data are incomparable weakens any blanket Section 58 defence built on condition records.',
          },
          {
            question: 'Newcastle filled nearly 80,000 potholes in five years — does that mean the roads are fixed?',
            answer: 'No. The council repaired 14,935 potholes in 2024/25 alone and 79,247 over five years — roughly 43 every day — while U-road RED condition doubled on comparable data and the council resurfaced just 0.84% of its road network in 2024/25. High repair volume is evidence of a network producing defects faster than structural maintenance can keep pace, not evidence your specific pothole was reasonably managed.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Newcastle City Council"
        councilSlug="newcastle"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/newcastle"
        headline="Newcastle Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Newcastle</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Newcastle: RED Best Practice, 100% Reactive Repairs
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Newcastle City Council matches its DfT capital allocation and earns a GREEN spend scorecard.
            Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — and the DfT flags
            its highway management as <strong className="text-rose-400">RED for best practice</strong>.
            The council's own transparency report records <strong className="text-rose-400">0% preventative
            spend in every published year</strong>, U-roads in RED condition more than doubled to 28.9% on
            comparable data, and <strong className="text-rose-400">79,247 potholes were filled in five years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">0%</div>
            <div className="text-white font-medium mt-2">Preventative highway spend — five years running</div>
            <div className="text-brand-300 text-sm mt-1">
              Newcastle's own figures classify 100% of capital and revenue highway spend as reactive
              from 2020/21 through to the 2025/26 forecast. Preventative treatment is described only
              as a future "aspiration".
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The RED Best Practice Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The RED Best-Practice Rating Reveals
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT's weakest management scorecard — backed by the council's own spending classifications
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The works we carry out on our highway are currently reactive treatments based on condition surveys, visual inspections, and reports from members of the public."
              source="Newcastle City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Going forward it is our aspiration to look at implementing preventative treatments into our highway maintenance programme such as surface dressing, slurry sealing and micro asphalting."
              source="Newcastle City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (forecast)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0%</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, the council must maintain its roads. Section 58
              lets it escape liability only if it can show a reasonable system of inspection and repair.
              A council that classifies every pound of highway spend as reactive — and describes preventative
              work as a future aspiration — is documenting a fix-it-when-it-breaks model, not proactive
              asset management.
            </p>
            <p className="text-white font-medium">
              The RED best-practice scorecard is not an opinion. It is the DfT's assessment of exactly
              what Newcastle's own figures describe.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition data from Newcastle's own transparency report — stable classified
            roads, then a methodology cliff in 2024
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (66.2km — 6.3% of network): stable, then 2024 spike*</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">14.9%</td>
                    <td className="px-4 py-2 text-center">83.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">18.6%</td>
                    <td className="px-4 py-2 text-center">78.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">17.2%</td>
                    <td className="px-4 py-2 text-center">79.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">16.5%</td>
                    <td className="px-4 py-2 text-center">81.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.96%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">57.38%</td>
                    <td className="px-4 py-2 text-center font-bold">36.66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              On comparable SCANNER-era data, A-roads held steady at 1.9–2.8% RED. The 2024/25 jump
              uses Gaist PAS 2161 surveys — not directly comparable per the council's own footnote.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (131.1km — 12.5% of network): same cliff*</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">1.5%</td>
                    <td className="px-4 py-2 text-center">14.3%</td>
                    <td className="px-4 py-2 text-center">84.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">1.4%</td>
                    <td className="px-4 py-2 text-center">15.6%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">1.05%</td>
                    <td className="px-4 py-2 text-center">11.7%</td>
                    <td className="px-4 py-2 text-center">87.25%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">1.35%</td>
                    <td className="px-4 py-2 text-center">12.05%</td>
                    <td className="px-4 py-2 text-center">86.6%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.51%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">59.5%</td>
                    <td className="px-4 py-2 text-center font-bold">30.99%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Comparable data shows B/C roads broadly stable at 1–1.5% RED. The 2024/25 figures — 9.51%
              RED and only 30.99% green — reflect a new survey methodology, not a like-for-like trend line.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, Minimal Structural Renewal</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.44m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.44m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">0.84%</div>
                <div className="text-brand-300 text-sm">Of network resurfaced in 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Newcastle matches its DfT allocation for 2025/26 and exceeded it in three of the previous
              four years. Yet in 2024/25 it reconstructed or resurfaced just{' '}
              <span className="text-rose-400">8,787 metres of 1,051.8km</span> — less than one per cent
              of the carriageway network. Money is arriving. Structural renewal is not keeping pace.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Deterioration */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 783.4km U-Road Problem
          </h2>
          <p className="text-surface-600 mb-8">
            Three-quarters of Newcastle's carriageway network — surveyed only once every two years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19.62%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28.9%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25*</td>
                  <td className="px-6 py-4 text-center font-medium">16.07% <span className="text-surface-400 text-xs">(new methodology — not comparable)</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">More Than Doubled On Comparable Data</h3>
              <p className="text-surface-600 text-sm mb-3">
                On SCANNER-era surveys, U-roads in RED condition rose from 14.17% in 2020/21 to
                28.9% in 2023/24 — more than doubling in four years. That is roughly{' '}
                <strong>226km of residential streets, estate roads and local routes</strong> in the
                worst condition band at the last comparable survey.
              </p>
              <p className="text-surface-600 text-sm">
                The council itself acknowledges "deteriorating assets" and "increasing traffic volumes"
                in its asset management strategy — while classifying every pound of spend as reactive.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Two-Year Survey Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Newcastle appointed Gaist Solutions Ltd in 2024 under a contract running until 2028.
                The council states: "Our network is surveyed every 2 years."
              </p>
              <p className="text-amber-800 text-sm">
                On a network where U-road RED condition doubled between comparable surveys, a biennial
                survey cycle means defects can form, grow, and cause damage in the gap — exactly the
                window Section 58 arguments turn on.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Despite making substantial progress in recent years, we recognise we are in an increasingly challenging environment, with deteriorating assets, increasing traffic volumes and uncertainty around future funding."
              source="Newcastle City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Newcastle's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it in a survey year or a gap year?</li>
              <li>• If 28.9% of U-roads were RED at the last comparable survey, what was done about yours?</li>
              <li>• How does a biennial survey catch a pothole that formed 18 months after the last pass?</li>
              <li>• Can the council even compare its own 2024 condition records with anything before the Gaist switch?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits deteriorating assets, surveys three-quarters of its network every
              other year, and spends nothing preventatively cannot claim comprehensive knowledge of
              every defect.
            </p>
          </div>
        </div>
      </section>

      {/* The Methodology Change */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2024 Survey Switch
          </h2>
          <p className="text-surface-600 mb-8">
            Newcastle changed how it measures roads — and says the old and new data cannot be compared
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="This data cannot be directly compared with historic data due to the change in the classification tier system."
              source="Newcastle City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Technical Survey provider changed from SCANNER, CVI/DVI Highway Condition Surveys to Gaist Condition Surveys with different assessment methodology in accordance with PAS 2161:2024 Guidance"
              source="Newcastle City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What Changed</h3>
              <p className="text-surface-600 text-sm mb-3">
                In 2024 Newcastle replaced SCANNER, CVI and DVI surveys with Gaist condition surveys
                under PAS 2161:2024. The council moved from a three-tier Red/Amber/Green system to
                a more detailed 1–5 classification — ahead of the DfT's mandatory switch in 2026/27.
              </p>
              <p className="text-surface-600 text-sm">
                Every 2024/25 condition figure in this report carries an asterisk for that reason.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What It Means For Claims</h3>
              <p className="text-amber-800 text-sm mb-3">
                When a council cannot compare its own before-and-after condition data, it weakens any
                blanket argument that the network was adequately managed. The deterioration trend on
                comparable U-road data (14.17% → 28.9% RED) stands on its own.
              </p>
              <p className="text-amber-800 text-sm">
                The 2024/25 headline figures — 5.96% RED A-roads, 9.51% RED B/C roads — use a different
                ruler. Do not let the council cherry-pick them without acknowledging the methodology break.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            79,247 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">15,240</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,059</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">12,834</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,179</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">14,935</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">79,247</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~43 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Newcastle fills around 43 potholes per day. The council
                estimates 15,849 repairs for 2025/26 based on this five-year average. A network producing
                defects at that rate is, by definition, one where potholes routinely form between
                inspections — exactly the scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Repairs ≠ Resurfacing</h3>
              <p className="text-amber-800 text-sm">
                Filling 79,247 potholes in five years while resurfacing just 0.84% of the network in
                2024/25 tells you where the money goes: reactive patches, not structural renewal.
                A filled pothole is evidence the road was failing — not evidence it was well maintained.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend + RED best practice means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Split-Score Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Newcastle funds its roads but manages them reactively — here's how that shapes your claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — meets or exceeds DfT capital allocation in most years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Transport Asset Management Plan covering 2024–2039
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Gaist condition surveys under PAS 2161 — modern survey technology
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 63 planned maintenance schemes for 2025/26 including 15.3km resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C roads broadly stable at 1–2% RED on comparable pre-2024 data
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on major classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — 0% preventative spend in every published year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-roads in RED more than doubled to 28.9% on comparable data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 74.5% of the network is U-roads — surveyed only every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79,247 potholes filled in five years — defects form faster than structural work
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "deteriorating assets" and reactive-only treatment model
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2024 methodology change — council cannot compare its own historic condition data
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but RED best practice, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the biennial survey gap and 28.9% RED figure are your strongest structural arguments</li>
              <li>• Whether the council's 0% preventative spend undermines its claim of reasonable maintenance on your route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Newcastle's own transparency data — including the RED best-practice rating and reactive-only
              spending — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Newcastle?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A split-score council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED best-practice argument</li>
                <li>• No U-road deterioration data</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 0% preventative spend documented</li>
                <li>✅ U-road RED doubling to 28.9% cited</li>
                <li>✅ 79,247 potholes in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Newcastle</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=newcastle"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Newcastle Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Newcastle has a GREEN spend rating — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and
                whether the council had reasonable knowledge of it under Section 58. Newcastle is AMBER
                overall for condition, RED for best practice, and its own report records 0% preventative
                spend and 100% reactive spend across every year published — meaning repairs happen after
                damage, not before.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Newcastle's RED best-practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is RED — one of the weakest ratings a council can hold.
                Newcastle's transparency report shows 0% of highway spend classified as preventative in
                every year from 2020/21 to 2025/26, with 100% reactive. The council itself states works
                are "currently reactive treatments" and that implementing preventative programmes is only
                an "aspiration". That undercuts any Section 58 argument that the council proactively
                managed the road before your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                My pothole was on a residential street — does the U-road data help?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 783.4km — 74.5% of Newcastle's 1,051.8km carriageway network.
                On comparable pre-2024 survey data, U-roads in RED condition rose from 14.17% in 2020/21
                to 28.9% in 2023/24 — more than doubling in four years. The network is surveyed only once
                every two years, so a defect can form and worsen in the gap between surveys.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The 2024 condition figures look worse — are they reliable?
              </h3>
              <p className="text-surface-600 text-sm">
                Treat 2024 separately. Newcastle switched survey provider from SCANNER/CVI/DVI to Gaist
                PAS 2161:2024 surveys in 2024, and the council states this data "cannot be directly
                compared with historic data due to the change in the classification tier system." The 2024
                A-road RED figure of 5.96% and B/C RED of 9.51% use a different ruler — but the council's
                own admission that historic and current data are incomparable weakens any blanket Section 58
                defence built on condition records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Newcastle filled nearly 80,000 potholes in five years — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council repaired 14,935 potholes in 2024/25 alone and 79,247 over five years —
                roughly 43 every day — while U-road RED condition doubled on comparable data and the council
                resurfaced just 0.84% of its road network in 2024/25. High repair volume is evidence of a
                network producing defects faster than structural maintenance can keep pace, not evidence your
                specific pothole was reasonably managed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://new.newcastle.gov.uk/sites/default/files/2025-06/Local%20Highway%20Maintenance%20Transparency%20Report%20202-26.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Newcastle City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
